export type ThankYouFormType = "contact-us" | "get-quote" | "career";

export type ThankYouSubmission = {
  formType: ThankYouFormType;
  name: string;
  email?: string;
  /** Extra context: service/solution name, job role title, etc. */
  contextLabel?: string;
  /** Selected service package name (quote form). */
  packageName?: string;
  /** Selected package amount / price label. */
  packageAmount?: string;
  /** CMS extra quote field answers (label + value). */
  extraFormFieldAnswers?: {
    type?: string;
    label?: string;
    value?: string;
  }[];
  /** Optional short note shown on the thank-you page. */
  summary?: string;
  submittedAt: string;
};

export const THANK_YOU_STORAGE_KEY = "bytelogic_thank_you_submission";

/** Survives React Strict Mode remounts after the first consume. */
let consumedSubmission: ThankYouSubmission | null | undefined;

export const THANK_YOU_FORM_LABELS: Record<ThankYouFormType, string> = {
  "contact-us": "Contact enquiry",
  "get-quote": "Quote request",
  career: "Job application",
};

export function storeThankYouSubmission(
  submission: Omit<ThankYouSubmission, "submittedAt"> & {
    submittedAt?: string;
  }
) {
  if (typeof window === "undefined") return;

  // Allow a new thank-you visit to pick up the latest submission.
  consumedSubmission = undefined;

  const payload: ThankYouSubmission = {
    ...submission,
    name: submission.name.trim(),
    email: submission.email?.trim() || undefined,
    contextLabel: submission.contextLabel?.trim() || undefined,
    packageName: submission.packageName?.trim() || undefined,
    packageAmount: submission.packageAmount?.trim() || undefined,
    extraFormFieldAnswers: Array.isArray(submission.extraFormFieldAnswers)
      ? submission.extraFormFieldAnswers
          .map((item) => ({
            type: item?.type?.trim() || undefined,
            label: item?.label?.trim() || undefined,
            value: item?.value?.trim() || undefined,
          }))
          .filter((item) => item.value)
      : undefined,
    summary: submission.summary?.trim() || undefined,
    submittedAt: submission.submittedAt || new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(THANK_YOU_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures (private mode / quota).
  }
}

export function readThankYouSubmission(): ThankYouSubmission | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(THANK_YOU_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ThankYouSubmission;
    if (!parsed?.formType || !parsed?.name) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Read once, clear storage, and keep the value in memory so Strict Mode
 * remounts still see the submission details.
 */
export function consumeThankYouSubmission(): ThankYouSubmission | null {
  if (typeof window === "undefined") return null;
  if (consumedSubmission !== undefined) return consumedSubmission;

  const data = readThankYouSubmission();
  clearThankYouSubmission();
  consumedSubmission = data;
  return data;
}

export function clearThankYouSubmission() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(THANK_YOU_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
